export const MAX_TICKET_SCREENSHOTS = 4;
export const MAX_TICKET_SCREENSHOT_BYTES = 2 * 1024 * 1024;
export const TICKET_SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

export type TicketScreenshotPayload = {
  name: string;
  content_type: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp';
  data_base64: string;
};

export type TicketScreenshotDraft = {
  id: string;
  name: string;
  previewUrl: string;
  payload: TicketScreenshotPayload;
};

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the image'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

function safeName(name: string, fallback: string): string {
  const trimmed = name.replace(/[^\w.\- ()[\]]+/g, '_').slice(0, 80).trim();
  return trimmed || fallback;
}

async function compressImage(file: File): Promise<{ blob: Blob; contentType: TicketScreenshotPayload['content_type'] }> {
  if (typeof createImageBitmap !== 'function') {
    if (file.size > 180 * 1024) {
      throw new Error('That image is too large for this browser. Try a smaller file.');
    }
    const type = TICKET_SCREENSHOT_TYPES.includes(file.type as TicketScreenshotPayload['content_type'])
      ? (file.type as TicketScreenshotPayload['content_type'])
      : 'image/jpeg';
    return { blob: file, contentType: type };
  }

  const bitmap = await createImageBitmap(file);
  try {
    // Each image is uploaded on its own. Stay well under the ~500 KB gateway body limit
    // after base64 (~180 KB binary ≈ 240 KB of JSON).
    const maxBytes = 180 * 1024;
    let edge = 1600;
    let quality = 0.82;
    let blob = await renderJpeg(bitmap, edge, quality);
    while (blob.size > maxBytes && (quality > 0.5 || edge > 720)) {
      if (quality > 0.5) quality = Math.round((quality - 0.08) * 100) / 100;
      else edge = Math.round(edge * 0.75);
      blob = await renderJpeg(bitmap, edge, quality);
    }
    if (blob.size > maxBytes) {
      throw new Error('That image is still too large. Try a smaller crop.');
    }
    return { blob, contentType: 'image/jpeg' };
  } finally {
    bitmap.close();
  }
}

function renderJpeg(source: ImageBitmap, maxEdge: number, quality: number): Promise<Blob> {
  const scale = Math.min(1, maxEdge / Math.max(source.width, source.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Could not process the screenshot'));
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Could not process the screenshot'));
    }, 'image/jpeg', quality);
  });
}

/** Compressed data URL for an image pasted into the ticket text. */
export async function fileToDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files can be added to the text.');
  }
  const { blob, contentType } = await compressImage(file);
  const data = await blobToBase64(blob);
  return `data:${contentType};base64,${data}`;
}

export async function fileToScreenshotDraft(file: File, index: number): Promise<TicketScreenshotDraft> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Only image files can be attached.');
  }
  const { blob, contentType } = await compressImage(file);
  const data_base64 = await blobToBase64(blob);
  const name = safeName(file.name, `screenshot-${index + 1}.jpg`);
  return {
    id: crypto.randomUUID(),
    name,
    previewUrl: URL.createObjectURL(blob),
    payload: { name, content_type: contentType, data_base64 },
  };
}

export function collectImageFiles(list: FileList | File[] | null | undefined): File[] {
  if (!list) return [];
  return [...list].filter((file) => file.type.startsWith('image/'));
}

export function revokeScreenshotUrls(drafts: TicketScreenshotDraft[]): void {
  for (const draft of drafts) {
    URL.revokeObjectURL(draft.previewUrl);
  }
}
